import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SignedImage from '@/components/ipe/SignedImage';
import '@/styles/temu-platform/temu-product-ops.css';

/**
 * TEMU 商品图大图预览（滚轮/按钮缩放、拖拽平移、Esc 关闭）。
 * 挂到 body，避免被弹窗 / 表格 overflow 遮挡。
 */
export default function TemuImagePreviewLightbox({ src, alt, preferSigned = false, onClose }) {
  const MIN_SCALE = 0.5;
  const MAX_SCALE = 5;
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const rootRef = useRef(null);

  const clampScale = (value) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(value * 100) / 100));

  const zoomBy = (delta) => {
    setScale((s) => clampScale(s + delta));
  };

  const resetZoom = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (!src) return undefined;
    setScale(1);
    setOffset({ x: 0, y: 0 });
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        setScale((s) => clampScale(s + 0.25));
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        setScale((s) => clampScale(s - 0.25));
      }
      if (e.key === '0') {
        e.preventDefault();
        setScale(1);
        setOffset({ x: 0, y: 0 });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [src, onClose]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !src) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const step = e.deltaY > 0 ? -0.15 : 0.15;
      setScale((s) => clampScale(s + step));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [src]);

  useEffect(() => {
    if (scale <= 1) setOffset({ x: 0, y: 0 });
  }, [scale]);

  if (!src) return null;

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    setOffset({
      x: drag.originX + (e.clientX - drag.startX),
      y: drag.originY + (e.clientY - drag.startY),
    });
  };

  const onPointerUp = (e) => {
    if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
  };

  const imgStyle = {
    transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
  };

  return createPortal(
    <div
      ref={rootRef}
      className="tpo-img-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={fixedUi("图片预览")}
      onClick={onClose}
    >
      <button type="button" className="tpo-img-lightbox__close" aria-label={fixedUi("关闭预览")} onClick={onClose}>
        ×
      </button>

      <div
        className="tpo-img-lightbox__toolbar"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={() => zoomBy(-0.25)} aria-label={fixedUi("缩小")} disabled={scale <= MIN_SCALE}>−</button>
        <button type="button" className="tpo-img-lightbox__scale" onClick={resetZoom} title={fixedUi("重置为 100%")}>
          {Math.round(scale * 100)}%
        </button>
        <button type="button" onClick={() => zoomBy(0.25)} aria-label={fixedUi("放大")} disabled={scale >= MAX_SCALE}>+</button>
      </div>

      <div
        className={`tpo-img-lightbox__stage${scale > 1 ? ' is-zoomable' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {preferSigned ? (
          <SignedImage
            url={src}
            alt={alt || fixedUi("预览")}
            className="tpo-img-lightbox__img"
            loading="eager"
            style={imgStyle}
            fallback={<span className="tpo-img-lightbox__empty">{fixedUi("图片无法预览")}</span>}
          />
        ) : (
          <img
            src={src}
            alt={alt || fixedUi("预览")}
            className="tpo-img-lightbox__img"
            draggable={false}
            referrerPolicy="no-referrer"
            style={imgStyle}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
